import { Icon } from "../icons";
import { fmtDate, calcOrm } from "../helpers";
import { HoldToDelete } from "./shared";

export default function ExerciseDetailModal({ exercise, onClose, onEdit, onDelete, onDeleteSet }) {
  const ex = exercise;
  const vol = ex.sets.reduce((a, s) => a + s.reps * s.weight, 0);
  const bestSet = ex.sets.reduce((best, s) =>
    s.weight > (best?.weight || 0) || (s.weight === (best?.weight || 0) && s.reps > (best?.reps || 0)) ? s : best, null);
  const estOrm = bestSet ? calcOrm(bestSet.weight, bestSet.reps) : null;

  // Group sets by date, preserving order (most recent last → reverse for display)
  const byDate = [];
  const dateMap = new Map();
  for (const s of ex.sets) {
    const key = s.date || "—";
    if (!dateMap.has(key)) { const g = { date: key, sets: [] }; dateMap.set(key, g); byDate.push(g); }
    dateMap.get(key).sets.push(s);
  }
  byDate.reverse();

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div>
            <div className="modal-title">{ex.name}</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {ex.muscle}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="modal-body">
          {/* Stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              ["1 Rep Max", ex.oneRepMax ? `${ex.oneRepMax} lbs` : estOrm ? `~${estOrm} lbs` : "—"],
              ["Total Sets", ex.sets.length],
              ["Top Set", bestSet ? `${bestSet.weight} × ${bestSet.reps}` : "—"],
              ["Total Vol", vol > 999 ? `${(vol / 1000).toFixed(1)}K lbs` : `${vol} lbs`],
            ].map(([k, v]) => (
              <div key={k} className="section-block" style={{ textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)" }}>{v}</div>
              </div>
            ))}
          </div>

          {/* History grouped by date */}
          {byDate.length > 0 && (
            <div>
              <div className="form-label" style={{ marginBottom: 12 }}>History</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {byDate.map(({ date, sets }) => (
                  <div key={date}>
                    {/* Date header */}
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: "var(--muted)",
                      textTransform: "uppercase", letterSpacing: "0.12em",
                      marginBottom: 8, paddingBottom: 4,
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                    }}>
                      {date !== "—" ? fmtDate(date) : "Unknown date"}
                    </div>

                    {/* Set rows */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {sets.map(s => {
                        const orm = calcOrm(s.weight, s.reps);
                        return (
                          <div key={s.id} style={{
                            display: "flex", alignItems: "center",
                            background: "var(--bg-highest)", borderRadius: 8,
                            padding: "10px 12px", gap: 12,
                          }}>
                            {/* Reps × Weight */}
                            <div style={{ flex: 1, display: "flex", alignItems: "baseline", gap: 6 }}>
                              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{s.reps}</span>
                              <span style={{ fontSize: 11, color: "var(--muted)" }}>reps</span>
                              <span style={{ fontSize: 12, color: "var(--muted)", margin: "0 2px" }}>@</span>
                              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                                {s.weight ? s.weight : "BW"}
                              </span>
                              <span style={{ fontSize: 11, color: "var(--muted)" }}>{s.weight ? "lbs" : ""}</span>
                            </div>

                            {/* Est 1RM */}
                            {orm && (
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)" }}>{orm} lbs</div>
                                <div style={{ fontSize: 9, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>est 1RM</div>
                              </div>
                            )}

                            {/* Delete */}
                            {onDeleteSet && (
                              <button
                                onClick={() => onDeleteSet(s.id)}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "rgba(255,100,100,0.35)", fontSize: 16,
                                  padding: "2px 4px", lineHeight: 1, flexShrink: 0,
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.color = "rgba(255,100,100,0.8)"}
                                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,100,100,0.35)"}
                                title="Delete set"
                              >✕</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ex.notes && (
            <div className="section-block">
              <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Notes</div>
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>{ex.notes}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <HoldToDelete onDelete={() => { onDelete(ex.id); onClose(); }} />
          <button className="btn btn-ghost btn-sm" onClick={() => { onClose(); onEdit(ex); }}><Icon.Edit /> Edit</button>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
